import { AudioClips, AUDIO_PATHS, MUSIC_PLAYLIST, MUSIC_PLAYS_PER_TRACK } from "./audio_constants.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { hapticsController } from "../haptics/haptics_controller.js";
import { shuffle } from "../utils/helpers.js";
import { SimulationContext } from "../simulation/simulation_context.js";

export enum AudioType
{
  MUSIC,
  SOUNDS,
  ALL
}

export class AudioController
{
  private readonly CROSSFADE_DURATION = 0.5;

  private ctx = new AudioContext();
  private master_gain = this.ctx.createGain();
  private base_volume = 1.0;
  private ducked = false;

  private state!: AudioType;

  private music_gain = this.ctx.createGain();
  private sound_gain = this.ctx.createGain();

  private music_volume = 0.5;
  private sounds_volume = 1.0;
  private fullscreen_ad_mute_depth = 0;
  private temporary_music_mute_depth = 0;

  private active_music: AudioBufferSourceNode | null = null;
  private active_music_gain: GainNode | null = null;
  private last_music_clip: AudioClips | null = null;

  private playlist_order: AudioClips[] = shuffle( [ ...MUSIC_PLAYLIST ] );
  private playlist_index = 0;
  private playlist_progress = 0;
  private playlist_src: AudioBufferSourceNode | null = null;
  private playlist_started_at = 0;
  private playlist_track_duration = 0;

  private buffers = new Map<AudioClips, AudioBuffer>();
  private inflight = new Map<AudioClips, Promise<AudioBuffer>>();
  private background_queue: AudioClips[] = [];
  private background_running = false;

  private readonly ESSENTIAL_CLIPS: AudioClips[] = [
    this.playlist_order[0],
    AudioClips.UI_CLICK,
  ];

  private downloaded_bytes = 0;

  public is_music_enabled = true;
  public is_sounds_enabled = true;

  public is_loading_finished = false;

  public TOTAL_COUNT = Object.values( AudioClips ).length;
  public LOADED_COUNT = 0;

  private audio_state_listeners: Array<() => void> = [];
  private _backgrounded = false;
  private _resume_pending = false;
  private _ctx_version = 0;
  private _awaiting_gesture_unlock = false;

  private async tryResumeContext( reason: string ): Promise<void>
  {
    try
    {
      await Promise.race( [
        this.ctx.resume(),
        new Promise<void>( resolve => setTimeout( resolve, 250 ) )
      ] );
    }
    catch ( error )
    {
      console.warn( "[Audio] ctx.resume failed", {
        reason,
        error
      } );
    }
  }

  constructor()
  {
    const storage = StatisticsStorage.load();
    this.is_music_enabled = storage.music_enabled;
    this.is_sounds_enabled = storage.sounds_enabled;

    this.changeState();

    this.music_gain.connect( this.master_gain );
    this.sound_gain.connect( this.master_gain );
    this.master_gain.connect( this.ctx.destination );

    this.master_gain.gain.value = this.base_volume;
    this.music_gain.gain.value = this.music_volume;

    this.ctx.addEventListener( 'statechange', () =>
    {
      const state = this.ctx.state as string;
      console.log( `[Audio] ctx statechange → ${state}, _backgrounded=${this._backgrounded}` );
      if ( state === 'running' && this._backgrounded && !this._awaiting_gesture_unlock )
      {
        void this._restoreMusicAfterResume();
      }
    } );
  }

  public changeState()
  {
    let type = AudioType.ALL;

    if ( ( this.is_music_enabled && this.is_sounds_enabled ) || ( !this.is_music_enabled && !this.is_sounds_enabled ) )
    {
      type = AudioType.ALL;
    }
    else if ( this.is_music_enabled && !this.is_sounds_enabled )
    {
      type = AudioType.MUSIC;
    }
    else if ( !this.is_music_enabled && this.is_sounds_enabled )
    {
      type = AudioType.SOUNDS;
    }

    this.state = type;
  }

  public getState(): AudioType
  {
    return this.state;
  }

  private load( clip: AudioClips ): Promise<AudioBuffer>
  {
    const cached = this.buffers.get( clip );
    if ( cached )
      return Promise.resolve( cached );

    const pending = this.inflight.get( clip );
    if ( pending )
      return pending;

    const queued = this.background_queue.indexOf( clip );
    if ( queued >= 0 )
      this.background_queue.splice( queued, 1 );

    const promise = this.fetchAndDecode( clip );
    this.inflight.set( clip, promise );

    return promise;
  }

  private async fetchAndDecode( clip: AudioClips ): Promise<AudioBuffer>
  {
    try
    {
      const path = AUDIO_PATHS[clip];
      const resp = await fetch( path );

      if ( !resp.ok && resp.status !== 0 )
        throw new Error( `http ${resp.status}` );

      const array_buf = await resp.arrayBuffer();

      if ( array_buf.byteLength === 0 )
        throw new Error( `empty response (status ${resp.status}) for ${path}` );

      this.downloaded_bytes += array_buf.byteLength;

      const audio_buf = await this.ctx.decodeAudioData( array_buf );

      this.buffers.set( clip, audio_buf );
      this.LOADED_COUNT++;

      return audio_buf;
    }
    catch ( error )
    {
      console.error( `[Audio] failed to load clip ${clip}`, error );

      const silent_buf = this.ctx.createBuffer( 1, 1, this.ctx.sampleRate );
      this.buffers.set( clip, silent_buf );
      this.LOADED_COUNT++;

      return silent_buf;
    }
    finally
    {
      this.inflight.delete( clip );
    }
  }

  public async loadEssential(): Promise<void>
  {
    await Promise.all( this.ESSENTIAL_CLIPS.map( clip => this.load( clip ) ) );
  }

  public startBackgroundLoading(): void
  {
    this.background_queue = Object.values( AudioClips ).filter(
      clip => !this.buffers.has( clip ) && !this.inflight.has( clip )
    );

    if ( this.background_running )
      return;

    this.background_running = true;
    void this.runBackgroundLoop();
  }

  private async runBackgroundLoop(): Promise<void>
  {
    while ( this.background_queue.length > 0 )
    {
      const clip = this.background_queue.shift()!;

      if ( !this.buffers.has( clip ) )
        await this.load( clip );

      // віддаємо кадр, щоб фонове вантаження не смикало картинку
      await ResourceManager.pacingDelay();
    }

    this.background_running = false;
    this.is_loading_finished = true;
  }

  public async LoadClips( clips: AudioClips[] )
  {
    for ( const clip of clips )
    {
      await this.load( clip );
    }
  }

  public async resumeIfNeeded()
  {
    const state = this.ctx.state as string;
    if ( state !== "suspended" && state !== "interrupted" )
    {
      return;
    }

    console.log( `[Audio] resumeIfNeeded — ctx.state=${state}, _backgrounded=${this._backgrounded}` );
    if ( state === "suspended" || state === "interrupted" )
    {
      await this.tryResumeContext( "resumeIfNeeded" );
      console.log( `[Audio] resumeIfNeeded — ctx resumed, new state=${this.ctx.state}` );
      await this._restoreMusicAfterResume();
    }
  }

  private async _restoreMusicAfterResume()
  {
    if ( !this._backgrounded || !this.is_music_enabled || !this.last_music_clip ) return;

    this._backgrounded = false;
    const clip = this.last_music_clip;
    console.log( `[Audio] _restoreMusicAfterResume — restarting clip=${clip}` );

    this.releasePlaylistTrack();
    if ( this.active_music )
    {
      try { this.active_music.stop(); } catch {}
      try { this.active_music.disconnect(); } catch {}
      this.active_music = null;
    }
    if ( this.active_music_gain )
    {
      try { this.active_music_gain.disconnect(); } catch {}
      this.active_music_gain = null;
    }
    this.last_music_clip = null;

    await this.playMusic( clip, true );
    console.log( `[Audio] _restoreMusicAfterResume — done, ctx.state=${this.ctx.state}` );
  }

  public async suspendForBackground()
  {
    this._backgrounded = true;
    const state = this.ctx.state as string;
    console.log( `[Audio] suspendForBackground — ctx.state=${state}, last_music_clip=${this.last_music_clip}` );
    try
    {
      if ( state !== "suspended" && state !== "interrupted" )
      {
        await this.ctx.suspend();
        console.log( `[Audio] suspendForBackground — ctx suspended, new state=${this.ctx.state}` );
      }
      else
      {
        console.log( `[Audio] suspendForBackground — already ${state}, skipped` );
      }
    }
    catch ( e )
    {
      console.warn( "[Audio] suspendForBackground — ctx.suspend() failed, stopping music", e );
      const saved_clip = this.last_music_clip;
      this.stopMusic();
      this.last_music_clip = saved_clip;
    }
  }

  public async resumeFromForeground()
  {
    console.log( `[Audio] resumeFromForeground — _resume_pending=${this._resume_pending}, _backgrounded=${this._backgrounded}, ctx.state=${this.ctx.state}, last_music_clip=${this.last_music_clip}, active_music=${!!this.active_music}` );

    if ( this._resume_pending )
    {
      console.log( "[Audio] resumeFromForeground — debounced, skipping" );
      return;
    }
    this._resume_pending = true;

    try
    {
      if ( this._backgrounded )
      {
        this._setupGestureUnlock();
      }
      console.log( `[Audio] resumeFromForeground — done, ctx.state=${this.ctx.state}` );
    }
    catch ( e )
    {
      console.warn( `[Audio] resumeFromForeground — failed`, e );
    }
    finally
    {
      this._resume_pending = false;
    }
  }

  private _setupGestureUnlock(): void
  {
    if ( this._awaiting_gesture_unlock ) return;
    this._awaiting_gesture_unlock = true;

    const saved_clip = this.last_music_clip;
    const was_playing = this.active_music !== null;

    const handler = () =>
    {
      document.removeEventListener( 'touchstart', handler, true );
      document.removeEventListener( 'pointerdown', handler, true );
      document.removeEventListener( 'mousedown', handler, true );

      this._awaiting_gesture_unlock = false;
      this._backgrounded = false;
      console.log( '[Audio] gesture unlock fired — creating new ctx in gesture' );

      this.releasePlaylistTrack();
      const old_ctx = this.ctx;
      try { void old_ctx.close(); } catch {}

      this.buffers.clear();
      this._ctx_version++;

      this.ctx = new AudioContext();

      this.master_gain = this.ctx.createGain();
      this.music_gain = this.ctx.createGain();
      this.sound_gain = this.ctx.createGain();
      this.music_gain.connect( this.master_gain );
      this.sound_gain.connect( this.master_gain );
      this.master_gain.connect( this.ctx.destination );
      this.master_gain.gain.value = this.ducked ? this.base_volume * 0.2 : this.base_volume;
      this.music_gain.gain.value = this.isMusicTemporarilyMuted() ? 0 : this.music_volume;
      this.sound_gain.gain.value = this.sounds_volume;

      this.ctx.addEventListener( 'statechange', () =>
      {
        const state = this.ctx.state as string;
        console.log( `[Audio] ctx statechange → ${state}, _backgrounded=${this._backgrounded}` );
        if ( state === 'running' && this._backgrounded && !this._awaiting_gesture_unlock )
        {
          void this._restoreMusicAfterResume();
        }
      } );

      void this.ctx.resume();
      this.background_running = false;
      this.startBackgroundLoading();

      const clip = this.last_music_clip ?? saved_clip;
      if ( this.is_music_enabled && clip )
      {
        this.last_music_clip = null;
        void this.playMusic( clip, true );
      }

      console.log( '[Audio] gesture unlock done — ctx.state=' + this.ctx.state );
    };

    document.addEventListener( 'touchstart', handler, { once: true, capture: true, passive: true } );
    document.addEventListener( 'pointerdown', handler, { once: true, capture: true, passive: true } );
    document.addEventListener( 'mousedown', handler, { once: true, capture: true } );
    console.log( '[Audio] gesture unlock listener registered, saved_clip=' + saved_clip );
  }

  public playMainMusic(): Promise<void>
  {
    return this.playMusic( this.playlist_order[this.playlist_index], true );
  }

  public playEscalatorMusic(): Promise<void>
  {
    return this.playMusic( AudioClips.MUSIC_ESCALATOR, true );
  }

  public async playMusic( clip: AudioClips, loop = true )
  {
    const is_playlist_track = MUSIC_PLAYLIST.includes( clip );
    if ( is_playlist_track )
      clip = this.playlist_order[this.playlist_index];

    console.log( '[Audio] playMusics', clip, loop, this.last_music_clip, this.active_music, this.is_music_enabled );
    if ( this.last_music_clip === clip && this.active_music )
    {
      return;
    }

    this.last_music_clip = clip;

    if ( !this.is_music_enabled )
    {
      return;
    }

    console.log( '[Audio] playMusic', clip, loop );

    await this.resumeIfNeeded();

    const buf = await this.load( clip );

    if ( !this.is_music_enabled )
    {
      return;
    }

    if ( this.ctx.state === 'suspended' )
    {
      await this.tryResumeContext( "playMusic before start" );
    }

    const crossfade_start_time = this.ctx.currentTime;
    const old_music = this.active_music;
    const old_gain = this.active_music_gain;
    if ( old_music && old_music === this.playlist_src )
      this.releasePlaylistTrack();

    const gain_node = this.ctx.createGain();
    gain_node.gain.value = 0.00001;
    gain_node.connect( this.music_gain );

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect( gain_node );
    src.loop = loop;

    const actual_start_time = Math.max( crossfade_start_time, this.ctx.currentTime );
    const is_playlist_loop = is_playlist_track && loop && buf.duration > 1;
    const start_offset = is_playlist_loop ? ( this.playlist_progress % 1 ) * buf.duration : 0;

    try
    {
      gain_node.gain.setValueAtTime( 0.00001, actual_start_time );
      const time_constant = this.CROSSFADE_DURATION / 5;
      gain_node.gain.setTargetAtTime( 1.0, actual_start_time, time_constant );
    }
    catch ( e )
    {
      console.warn( '[Audio] Failed to set fade in', e );
      gain_node.gain.value = 1.0;
    }

    try
    {
      src.start( actual_start_time, start_offset );
    }
    catch ( error )
    {
      console.error( "[Audio] music source start failed", {
        clip,
        ctx_state: this.ctx.state,
        error
      } );
      return;
    }

    this.active_music = src;
    this.active_music_gain = gain_node;

    if ( is_playlist_loop )
    {
      const time_left = Math.max( buf.duration - start_offset, ( MUSIC_PLAYS_PER_TRACK - this.playlist_progress ) * buf.duration );
      this.playlist_src = src;
      this.playlist_started_at = actual_start_time;
      this.playlist_track_duration = buf.duration;
      src.stop( actual_start_time + time_left );
      src.onended = () => this.onPlaylistTrackEnded( src );
    }

    if ( this.ctx.state !== 'running' )
    {
      this._setupGestureUnlock();
    }

    if ( old_music && old_gain )
    {
      try
      {
        const current_gain = old_gain.gain.value;

        if ( current_gain < 0.05 )
        {
          setTimeout( () =>
          {
            try
            {
              old_music.stop();
              old_music.disconnect();
              old_gain.disconnect();
            }
            catch ( e )
            {
              console.warn( '[Audio] Failed to cleanup old music', e );
            }
          }, 50 );
        }
        else
        {
          const fade_from = current_gain < 0.1 ? 1.0 : current_gain;

          old_gain.gain.cancelScheduledValues( actual_start_time );
          old_gain.gain.setValueAtTime( fade_from, actual_start_time );

          const time_constant = this.CROSSFADE_DURATION / 5;
          old_gain.gain.setTargetAtTime( 0.00001, actual_start_time, time_constant );

          setTimeout( () =>
          {
            try
            {
              old_music.stop();
              old_music.disconnect();
              old_gain.disconnect();
            }
            catch ( e )
            {
              console.warn( '[Audio] Failed to cleanup old music', e );
            }
          }, this.CROSSFADE_DURATION * 1000 + 200 );
        }
      }
      catch ( e )
      {
        console.warn( '[Audio] Failed to set fade out ramp', e );
        old_gain.gain.value = 0.0001;
      }
    }
  }

  private releasePlaylistTrack(): void
  {
    if ( !this.playlist_src )
      return;

    const elapsed = Math.max( 0, this.ctx.currentTime - this.playlist_started_at );
    this.playlist_progress += elapsed / this.playlist_track_duration;
    this.playlist_src = null;

    if ( this.playlist_progress >= MUSIC_PLAYS_PER_TRACK - 1e-6 )
      this.advancePlaylist();
  }

  private advancePlaylist(): void
  {
    this.playlist_index++;
    if ( this.playlist_index >= this.playlist_order.length )
      this.reshufflePlaylist();

    this.playlist_progress = 0;
    console.log( `[Audio] playlist → ${this.playlist_order[this.playlist_index]}` );
  }

  private reshufflePlaylist(): void
  {
    const last_clip = this.playlist_order[this.playlist_order.length - 1];
    this.playlist_order = shuffle( [ ...MUSIC_PLAYLIST ] );
    if ( this.playlist_order.length > 1 && this.playlist_order[0] === last_clip )
      [this.playlist_order[0], this.playlist_order[1]] = [this.playlist_order[1], this.playlist_order[0]];

    this.playlist_index = 0;
  }

  private onPlaylistTrackEnded( src: AudioBufferSourceNode ): void
  {
    if ( src !== this.playlist_src || src !== this.active_music )
      return;

    this.playlist_src = null;
    this.active_music = null;
    try { this.active_music_gain?.disconnect(); } catch {}
    this.active_music_gain = null;

    this.advancePlaylist();
    void this.playMainMusic();
  }

  public stopMusic()
  {
    this.releasePlaylistTrack();
    if ( this.active_music && this.active_music_gain )
    {
      try
      {
        const time_constant = this.CROSSFADE_DURATION / 5;
        this.active_music_gain.gain.setTargetAtTime( 0.00001, this.ctx.currentTime, time_constant );
      }
      catch
      {
        this.active_music_gain.gain.value = 0.00001;
      }

      const music_to_stop = this.active_music;
      const gain_to_stop = this.active_music_gain;

      setTimeout( () =>
      {
        try
        {
          music_to_stop.stop();
          music_to_stop.disconnect();
          gain_to_stop.disconnect();
        }
        catch ( e )
        {
          console.warn( '[Audio] Failed to stop music', e );
        }
      }, this.CROSSFADE_DURATION * 1000 );

      this.active_music = null;
      this.active_music_gain = null;
    }
  }

  public getCurrentMusic(): AudioClips | null
  {
    return this.last_music_clip;
  }

  public async playSound( clip: AudioClips, loop = false, volume = 1 )
  {
    if ( SimulationContext.enabled ) return;

    void hapticsController.vibrateForSound( clip );
    if ( !this.is_sounds_enabled ) return;

    await this.resumeIfNeeded();

    const buf = await this.load( clip );
    const src = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    src.buffer = buf;
    src.loop = loop;
    gain.gain.value = Math.max( 0, Math.min( 1, volume ) );
    src.connect( gain );
    gain.connect( this.sound_gain );
    src.start();
  }

  public async enableMusic( status: boolean, clip?: AudioClips, is_play: boolean = true )
  {
    this.is_music_enabled = status;

    StatisticsStorage.update( { music_enabled: status } );
    this.notifyAudioStateListeners();

    if ( status && is_play )
    {
      await this.resumeIfNeeded();

      const clip_to_play = clip || this.last_music_clip || this.playlist_order[this.playlist_index];
      await this.playMusic( clip_to_play, true );
    } else
    {
      this.stopMusic();
    }
  }

  public isEnabledSomething(): boolean
  {
    return this.is_music_enabled || this.is_sounds_enabled;
  }

  public async enableAll( status: boolean, is_play: boolean = true )
  {
    await this.enableMusic( status, undefined, is_play );
    await this.enableSounds( status );
  }

  public setMasterVolume( vol: number )
  {
    this.base_volume = Math.max( 0, Math.min( 1, vol ) );
    const target = this.ducked ? this.base_volume * 0.2 : this.base_volume;
    try
    {
      this.master_gain.gain.setTargetAtTime( target, this.ctx.currentTime, 0.01 );
    } catch
    {
      this.master_gain.gain.value = target;
    }
  }

  public setMusicVolume( vol: number )
  {
    this.music_volume = Math.max( 0, Math.min( 1, vol ) );
    this.applyMusicVolume();
  }

  /** Keeps game music silent while a native fullscreen ad owns the screen. */
  public muteForFullscreenAd(): void
  {
    this.fullscreen_ad_mute_depth++;
    this.applyMusicVolume();
  }

  public unmuteAfterFullscreenAd(): void
  {
    this.fullscreen_ad_mute_depth = Math.max( 0, this.fullscreen_ad_mute_depth - 1 );
    this.applyMusicVolume();
  }

  public muteMusicTemporarily(): void
  {
    this.temporary_music_mute_depth++;
    this.applyMusicVolume();
  }

  public unmuteMusicTemporarily(): void
  {
    this.temporary_music_mute_depth = Math.max( 0, this.temporary_music_mute_depth - 1 );
    this.applyMusicVolume();
  }

  private isMusicTemporarilyMuted(): boolean
  {
    return this.fullscreen_ad_mute_depth > 0 || this.temporary_music_mute_depth > 0;
  }

  private applyMusicVolume(): void
  {
    const is_muted = this.isMusicTemporarilyMuted();
    const target = is_muted ? 0 : this.music_volume;
    try
    {
      this.music_gain.gain.cancelScheduledValues( this.ctx.currentTime );
      if ( is_muted )
        this.music_gain.gain.setValueAtTime( 0, this.ctx.currentTime );
      else
        this.music_gain.gain.setTargetAtTime( target, this.ctx.currentTime, 0.01 );
    } catch
    {
      this.music_gain.gain.value = target;
    }
  }

  public setSoundsVolume( vol: number )
  {
    this.sounds_volume = Math.max( 0, Math.min( 1, vol ) );
    try
    {
      this.sound_gain.gain.setTargetAtTime( this.sounds_volume, this.ctx.currentTime, 0.01 );
    } catch
    {
      this.sound_gain.gain.value = this.sounds_volume;
    }
  }

  public duck()
  {
    this.ducked = true;
    const target = this.base_volume * 0.2;
    try
    {
      this.master_gain.gain.setTargetAtTime( target, this.ctx.currentTime, 0.01 );
    } catch
    {
      this.master_gain.gain.value = target;
    }
  }

  public unduck()
  {
    this.ducked = false;
    const target = this.base_volume;
    try
    {
      this.master_gain.gain.setTargetAtTime( target, this.ctx.currentTime, 0.01 );
    } catch
    {
      this.master_gain.gain.value = target;
    }
  }

  public async enableSounds( status: boolean )
  {
    this.is_sounds_enabled = status;

    StatisticsStorage.update( { sounds_enabled: status } );
    this.notifyAudioStateListeners();

    if ( status )
    {
      await this.resumeIfNeeded();
    }
  }

  public isLoadingFinished()
  {
    return this.is_loading_finished;
  }

  public getDownloadedBytes(): number
  {
    return this.downloaded_bytes;
  }

  public addAudioStateListener( callback: () => void ): void
  {
    this.audio_state_listeners.push( callback );
  }

  public removeAudioStateListener( callback: () => void ): void
  {
    const index = this.audio_state_listeners.indexOf( callback );
    if ( index > -1 )
    {
      this.audio_state_listeners.splice( index, 1 );
    }
  }

  private notifyAudioStateListeners(): void
  {
    this.audio_state_listeners.forEach( callback => callback() );
  }
}

export const audioController = new AudioController();
